import React from 'react';
import { connect } from 'react-redux';
import { isEmpty, omit } from 'lodash';
import { GroupsFilterableGrid } from '../GroupsFilterableGrid';
import { AccountsFilterableGrid } from '../AccountsFilterableGrid';
import { KpiEntryForm } from '../KpiEntryForm';
import {
  kpiEntriesByIdSelector,
  kpiGroupsSelector,
  kpiGroupsByIdSelector,
} from 'selectors/kpis';
import { openModal } from 'actions/modalActions';
import { addUpdateKpi } from 'actions/kpiActions';
import kpiValidation from 'validation/kpiValidation';
import kpis from 'utils/kpis';
import { findParentGroupIdOfKpiEntry } from 'utils/stateToState';

class KpiEntryModal extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = props.kpiEntry
      ? {
          showGroups: true,
          validationMessages: [],
          kpiEntry: {
            id: this.props.data.kpiEntryId,
            groupId: findParentGroupIdOfKpiEntry(
              this.props.kpiGroupsById,
              this.props.data.kpiEntryId
            ),
            ...props.kpiEntry,
          },
        }
      : {
          showGroups: true,
          validationMessages: [],
          kpiEntry: {
            id: -1,
            groupId: props.kpiGroups[0]?.id,
            name: '',
            equation: '',
            periodDaysParameter: '',
            parameterAssignments: {},
          },
        };
  }

  onChangeView = () => {
    this.setState((prevState) => ({ showGroups: !prevState.showGroups }));
  };

  handleUpdate = (update) => {
    this.setState((prevState) => ({
      kpiEntry: {
        ...prevState.kpiEntry,
        ...update,
      },
    }));
  };

  resetParameterAssignments = () => {
    const { parameterAssignments } = this.state.kpiEntry;
    const clearedParameterAssignments = { ...parameterAssignments };

    Object.keys(clearedParameterAssignments).forEach(
      (parameterAssignmentKey) => {
        const parameterAssignment =
          clearedParameterAssignments[parameterAssignmentKey];

        clearedParameterAssignments[parameterAssignmentKey] = omit(
          parameterAssignment,
          'parameter'
        );
      }
    );

    return clearedParameterAssignments;
  };

  handleEquationChange = (equation) => {
    const newParameterAssignments = this.resetParameterAssignments();

    this.setState((prevState) => ({
      kpiEntry: {
        ...prevState.kpiEntry,
        equation,
        parameterAssignments: newParameterAssignments,
        periodDaysParameter: '',
      },
    }));
  };

  addEntryItem = (contentType, contentId, contentDescription) => {
    const { kpiEntry } = this.state;

    if (contentId < 0) {
      // id = -1 for Unassgined accounts virtual group
      return;
    }

    let newId = -1;
    while (kpiEntry.parameterAssignments.hasOwnProperty(newId)) {
      newId -= 1;
    }
    this.handleUpdate({
      parameterAssignments: {
        ...kpiEntry.parameterAssignments,
        [newId]: {
          entryId: kpiEntry.id,
          contentType,
          contentId,
          contentDescription,
        },
      },
    });
  };

  checkValidity = () => {
    const {
      name,
      equation,
      parameterAssignments,
      periodDaysParameter,
    } = this.state.kpiEntry;

    const assignmentParameters = Object.values(parameterAssignments)
      .filter(
        (parameterAssignment) =>
          !!parameterAssignment.parameter && parameterAssignment.parameter
      )
      .map((parameterAssignment) => parameterAssignment.parameter);

    if (periodDaysParameter) {
      assignmentParameters.push(periodDaysParameter);
    }

    const validationMessages = [];

    if (!kpiValidation.isNameValid(name)) {
      validationMessages.push('Please enter a name for the KPI');
    }

    if (!kpiValidation.isEquationValid(equation)) {
      validationMessages.push('Please enter a valid equation for the KPI');
    }

    if (
      !kpiValidation.areAllParametersPicked(
        kpis.getParameters(equation),
        assignmentParameters
      )
    ) {
      validationMessages.push('Please pick all parameters for the KPI');
    }

    if (!kpiValidation.areDuplicatedValuesInArray(assignmentParameters)) {
      validationMessages.push(
        'Please remove all duplicated assigned parameters in the KPI'
      );
    }

    return validationMessages;
  };

  onOkClick = () => {
    const { onClose, addUpdateKpi } = this.props;
    const { kpiEntry } = this.state;

    const validationMessages = this.checkValidity();
    const hasValidationMessages = !isEmpty(validationMessages);

    if (hasValidationMessages) {
      this.setState({ validationMessages });
    } else {
      addUpdateKpi(kpiEntry);
      onClose();
    }
  };

  renderValidationMessages = () => {
    const { validationMessages } = this.state;

    return (
      <div className="validation-messages">
        <span className="validation-warning">
          Fix these errors before submitting the KPI:
        </span>
        <ul>
          {validationMessages.map((validationMessage, index) => (
            <li key={index}>{validationMessage}</li>
          ))}
        </ul>
      </div>
    );
  };

  renderGrid = () => {
    const { showGroups } = this.state;
    const groupsFilterableGrid = (
      <GroupsFilterableGrid onSelect={this.addEntryItem} canSelectGroup />
    );
    const accountsFilterableGrid = (
      <AccountsFilterableGrid onSelect={this.addEntryItem} />
    );

    if (showGroups) {
      return groupsFilterableGrid;
    }

    return accountsFilterableGrid;
  };

  render() {
    const { onClose, kpiGroups } = this.props;
    const { kpiEntry, validationMessages } = this.state;
    const { id } = kpiEntry;

    const isNew = id === -1;
    const hasValidationMessages = !isEmpty(validationMessages);

    if (!kpiGroups.length) {
      return (
        <div>
          <p>Cannot add KPI entry if there are no KPI Groups defined.</p>
          <button onClick={onClose}>OK</button>
        </div>
      );
    }

    return (
      <div className="KpiEntryModal">
        <div className="left-half">
          <h2>{isNew ? 'New ' : ''}KPI</h2>
          <KpiEntryForm
            kpiEntry={kpiEntry}
            kpiGroups={kpiGroups}
            onUpdate={this.handleUpdate}
            onEquationChange={this.handleEquationChange}
          />
          {hasValidationMessages && this.renderValidationMessages()}
          <div className="buttons">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn" onClick={this.onOkClick}>
              OK
            </button>
          </div>
        </div>
        <div className="right-half">
          <button type="button" className="btn" onClick={this.onChangeView}>
            Change View
          </button>
          {this.renderGrid()}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state, { data }) {
  return {
    kpiEntry: kpiEntriesByIdSelector(state)[data.kpiEntryId],
    kpiGroups: kpiGroupsSelector(state),
    kpiGroupsById: kpiGroupsByIdSelector(state),
  };
}

const mapDispatchToProps = {
  addUpdateKpi,
  openModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(KpiEntryModal);
